import * as React from 'react';
import { WPSClient, getWidgetProps, RequestContext, DestructuredURL, NavLink, Args, NavLanguage, safeBool, StringMap, withArgs } from 'DVWebNodeUtils';
import { Logo, MainMenu, SearchBar, LocaleSelector, YourAccount, Placeholder, SignUpCTA } from './components';
import { LocaleSelectionSubMenuProps } from './components/YourAccount';
import transformNav from './transforms';
import { NavProfiles } from '../Shared/transforms/Profiles';

require('./index.module.scss');
// Client-side only Javascript
if (!global.__SERVER__) {
  require('./scripts/nav');
}

interface LogoProps {
  targetUrl: string
  altText: string
  imageURL?: string
}

interface MainMenuProps {
  text: string
  links: NavLink[]
  isElcano: boolean
}

interface SearchHistoryStrings {
  recent: string,
  suggested: string,
  clear: string,
  clearInfoTitle: string,
  clearInfoButton: string,
  clearInfoMessage: string,
  cancel: string,
  clearInfoAck: string
}

interface SearchHistory {
  treatment: string,
  strings: SearchHistoryStrings
}

interface SearchBarProps {
  hasSignUpCTA: Boolean;
  searchFormAction: DestructuredURL,
  searchFormPlaceholder: string,
  searchPhrase: string,
  searchSuggestionsEndpoint: DestructuredURL,
  altText: string,
  searchHistory: SearchHistory
}

interface YourAccountProps {
  altText: string,
  links: NavLink[],
  localeSelectionSubMenu: LocaleSelectionSubMenuProps,
  strings: StringMap
}

interface LocaleSelectorProps {
  text: string,
  locales: NavLink[],
  languages: NavLanguage[],
  languageToken: string
}

interface SignUpCTAProps {
  text: string,
  href: string
}

export interface NavParamsProps {
  firstName: string,
  languageToken: string,
  signInLink: string,
  searchHistoryTreatment: string,
  isElcano: boolean
}

export interface NavProps {
    logo: LogoProps,
    mainMenu?: MainMenuProps,
    searchBar?: SearchBarProps,
    yourAccount?: YourAccountProps,
    cerberus?: NavProfiles,
    localeSelector?: LocaleSelectorProps,
    signUpCTA?: SignUpCTAProps,
    context: RequestContext,
}

const getProps: getWidgetProps<NavProps> =
    global.__SERVER__ || global.__NATIVEAPP__
        ? async ({ context, searchPhrase, firstName, pageGating, isCrow, isElcano, languageToken, nodeCache, signInLink, searchHistoryTreatment, isProfilesEnabled }, api) => {
              const isCerberusEnabledSafe = safeBool(isProfilesEnabled)
              const isElcanoSafe = safeBool(isElcano)
              const wpsClient = new WPSClient(api)
              const params = { firstName, languageToken, signInLink, searchHistoryTreatment, isElcano: isElcanoSafe }
              const [navResponse, getProfilesResponse] = await Promise.all([
                wpsClient.Navigation({ context, pageGating, searchPhrase, isCrow: safeBool(isCrow), isElcano: isElcanoSafe }, nodeCache),
                (isCerberusEnabledSafe ? wpsClient.GetProfiles({ withAdditionalData: true, context }) : null)])
              return transformNav(navResponse, context, params, {
                isCerberusEnabled: isCerberusEnabledSafe, 
                data: getProfilesResponse})
          }
        : undefined;

const NavWidget = ({
    logo,
    mainMenu,
    searchBar,
    yourAccount,
    cerberus,
    localeSelector,
    context,
    signUpCTA,
    logger
  }: NavProps & Args) => {
    const cerberusContainerClass = cerberus ? "pv-nav-cerberus" : ''
    const cerberusKidsContainerClass = cerberus && cerberus.activeProfile.ageGroup === "CHILD" ? "pv-nav-cerberus-kids" : ''
    
    return (
        <div className={`pv-nav-container ${cerberusContainerClass} ${cerberusKidsContainerClass}`}>
          <Logo {...logo} />
          <div className="pv-nav-primary-components">
            {mainMenu ? <MainMenu {...mainMenu} /> : <Placeholder />}
            {searchBar && <SearchBar {...searchBar} logger={logger} context={context} />}
            {signUpCTA && <SignUpCTA {...signUpCTA}/>}
            {localeSelector && <LocaleSelector {...localeSelector} />}
            <YourAccount {...yourAccount} vtr={context.currentTerritory} cerberus={cerberus} />
          </div>
        </div>
    )
  }

export const Nav = {
    name: 'nav',
    getProps,
    Component: withArgs(NavWidget) as React.ComponentType<NavProps>,
    container: {
      tagName: 'header',
      ID: 'pv-nav',
      classes: ['pv-nav', 'av-blur-backdrop']
    }
};
